import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Key,
  Loader2,
  RefreshCw,
  Filter,
  Copy,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fmtServerDateTime as fmtTime } from './utils/datetime';

const EVENT_BADGES = {
  created:            { label: 'Created',        cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  created_bulk:       { label: 'Bulk Created',   cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  updated:            { label: 'Updated',        cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  deleted:            { label: 'Deleted',        cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  revoked:            { label: 'Revoked',        cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  resent:             { label: 'Resent',         cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  redeemed:           { label: 'Redeemed',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  upgraded_with_code: { label: 'Upgraded (Code)', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  upgraded_razorpay:  { label: 'Upgraded (Paid)', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
};

export function AdminLicenseLogsPage() {
  const { admin, loading } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [fetching, setFetching] = useState(true);
  const [eventType, setEventType] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (eventType) params.set('event_type', eventType);
      if (search.trim()) params.set('search', search.trim());
      const r = await adminApi.get(`/admin/license-logs?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      toast.error('Could not load license logs');
    } finally {
      setFetching(false);
    }
  }, [page, pageSize, eventType, search]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div
      data-testid="admin-license-logs-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin/licenses" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="license-logs-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Key className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Licensing Logs</div>
            <div className="text-xs text-slate-400">Audit trail of every license event — create, update, revoke, redeem, upgrade</div>
          </div>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="license-logs-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-400 uppercase tracking-wider">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <select
            data-testid="license-logs-event-filter"
            value={eventType}
            onChange={(e) => { setEventType(e.target.value); setPage(1); }}
            className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
          >
            <option value="">All events</option>
            {Object.keys(EVENT_BADGES).map((k) => (
              <option key={k} value={k}>{EVENT_BADGES[k].label}</option>
            ))}
          </select>
          <Input
            data-testid="license-logs-search"
            placeholder="Search by code / order / email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); refresh(); }}}
            className="bg-slate-800/50 border-slate-700 text-white text-sm h-9 max-w-xs"
          />
          <span className="text-xs text-slate-500 ml-auto">
            {total.toLocaleString('en-IN')} event{total === 1 ? '' : 's'}
          </span>
        </div>

        {/* Table */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
          {fetching ? (
            <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No log entries match your filters.</div>
          ) : (
            <table className="w-full text-sm" data-testid="license-logs-table">
              <thead className="bg-slate-950/60">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 text-left w-44">Time</th>
                  <th className="px-4 py-2 text-left w-40">Event</th>
                  <th className="px-4 py-2 text-left">License</th>
                  <th className="px-4 py-2 text-left">Actor</th>
                  <th className="px-4 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((log) => {
                  const badge = EVENT_BADGES[log.event_type] || { label: log.event_type, cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' };
                  const isExpanded = expanded === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        data-testid={`license-log-row-${log.id}`}
                        className="text-slate-200 hover:bg-slate-800/30 cursor-pointer"
                        onClick={() => setExpanded(isExpanded ? null : log.id)}
                      >
                        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{fmtTime(log.created_at)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            {log.license_code ? (
                              <span className="font-mono text-xs text-amber-300 truncate">{log.license_code}</span>
                            ) : <span className="text-slate-500 text-xs">—</span>}
                            {log.order_id && (
                              <span className="text-xs text-slate-500 truncate">· {log.order_id}</span>
                            )}
                            {log.tier && (
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                                {log.tier}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                              log.actor_type === 'superadmin' ? 'bg-amber-500/15 text-amber-300' :
                              log.actor_type === 'user' ? 'bg-emerald-500/15 text-emerald-300' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {log.actor_type}
                            </span>
                            <span className="text-slate-400 truncate">{log.actor_email || '—'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-slate-500">
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-950/40" data-testid={`license-log-details-${log.id}`}>
                          <td colSpan={5} className="px-6 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              {log.license_id && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500 w-24">license_id</span>
                                  <span className="font-mono text-slate-300 truncate">{log.license_id}</span>
                                  <button onClick={(e) => { e.stopPropagation(); copy(log.license_id, 'license_id'); }}>
                                    <Copy className="w-3 h-3 text-slate-500 hover:text-slate-300" />
                                  </button>
                                </div>
                              )}
                              {log.organization_id && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500 w-24">org_id</span>
                                  <span className="font-mono text-slate-300 truncate">{log.organization_id}</span>
                                </div>
                              )}
                              {log.actor_id && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500 w-24">actor_id</span>
                                  <span className="font-mono text-slate-300 truncate">{log.actor_id}</span>
                                </div>
                              )}
                            </div>
                            {log.details && Object.keys(log.details).length > 0 && (
                              <div className="mt-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">details</div>
                                <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-emerald-200 overflow-auto max-h-60">{JSON.stringify(log.details, null, 2)}</pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
